import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LegalLinks } from "./legal/LegalLinks";
import "./AcceptInvitationPage.css";

interface InvitationDetails {
  email: string;
  orgId: string;
  orgName: string;
  role: "OWNER" | "ADMIN" | "MEMBER";
  invitedByName: string;
  expiresAt: string;
}

type Status =
  | "loading"
  | "valid"
  | "invalid"
  | "accepting"
  | "accepted"
  | "declining"
  | "declined"
  | "error";

/**
 * Public landing page for /invitations/accept?token=…
 *
 * Renders OUTSIDE ContextGuard (see App.tsx) so an invitee who has never
 * signed in can preview the invitation before deciding to sign up. Flow:
 *
 *   1. Fetch /api/invitations/details?token=… (no auth) → show org/role/inviter.
 *   2. If unauth: "Sign in to accept" button hands off to Auth0 with a
 *      returnTo pointing at this same URL — after signup we come back
 *      to step 3 with an authenticated session.
 *   3. If auth: "Accept" POSTs to /api/invitations/accept, "Decline"
 *      POSTs to /api/invitations/:id/decline. On accept, navigate home;
 *      the org-context provider will pick up the new membership on its
 *      next fetch.
 *
 * The email-mismatch case (invited as alice@x.com, signed in as
 * bob@x.com) surfaces from the /accept endpoint as a specific error —
 * we show it inline with a "sign out and try again" hint.
 */
export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { authenticated, loading: authLoading, login, logout, token: authToken } = useAuth();

  const [status, setStatus] = useState<Status>("loading");
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invitationId, setInvitationId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setErrorMessage("No invitation token provided.");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `/api/invitations/details?token=${encodeURIComponent(token)}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setStatus("invalid");
          setErrorMessage(
            (body as { error?: string }).error ||
              "This invitation is no longer valid.",
          );
          return;
        }
        const body = (await res.json()) as { invitation: InvitationDetails };
        setDetails(body.invitation);
        setStatus("valid");
      } catch (err) {
        setStatus("error");
        setErrorMessage(err instanceof Error ? err.message : "Failed to load invitation");
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    setStatus("accepting");
    setErrorMessage(null);
    try {
      const res = await fetch("/api/invitations/accept", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrorMessage(
          (body as { error?: string }).error || "Could not accept invitation.",
        );
        setStatus("valid");
        return;
      }
      setStatus("accepted");
      // Give the OrganizationProvider a moment to refresh with the new
      // membership on the next page mount, then send them home.
      setTimeout(() => navigate("/"), 1200);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to accept");
      setStatus("valid");
    }
  };

  const handleDecline = async () => {
    // We need the invitation's tokenId (row id), not the JWT token, to
    // decline. Fetch pending-for-me and match — cheap enough for the
    // rare decline path, and keeps the /details preview endpoint from
    // leaking the row id publicly.
    if (!authenticated) return;
    setStatus("declining");
    setErrorMessage(null);
    try {
      let id = invitationId;
      if (!id) {
        const listRes = await fetch("/api/invitations/pending-for-me", {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (listRes.ok) {
          const list = (await listRes.json()) as {
            invitations: Array<{ id: string; token: string }>;
          };
          id = list.invitations.find((i) => i.token === token)?.id ?? null;
          if (id) setInvitationId(id);
        }
      }
      if (!id) {
        setErrorMessage("Could not resolve invitation id.");
        setStatus("valid");
        return;
      }
      const res = await fetch(`/api/invitations/${id}/decline`, {
        method: "POST",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrorMessage(
          (body as { error?: string }).error || "Could not decline invitation.",
        );
        setStatus("valid");
        return;
      }
      setStatus("declined");
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to decline");
      setStatus("valid");
    }
  };

  if (status === "loading" || authLoading) {
    return (
      <div className="accept-invite">
        <div className="accept-invite__card">
          <p>Loading invitation…</p>
        </div>
      </div>
    );
  }

  if (status === "invalid" || status === "error") {
    return (
      <div className="accept-invite">
        <div className="accept-invite__card">
          <h1>Invitation unavailable</h1>
          <p>{errorMessage}</p>
          <p className="accept-invite__hint">
            If you think this is a mistake, ask the person who invited you to
            send a fresh link.
          </p>
          <button className="accept-invite__btn" onClick={() => navigate("/")}>
            Go home
          </button>
        </div>
      </div>
    );
  }

  if (status === "accepted") {
    return (
      <div className="accept-invite">
        <div className="accept-invite__card">
          <h1>You're in.</h1>
          <p>Welcome to {details?.orgName}. Redirecting…</p>
        </div>
      </div>
    );
  }

  if (status === "declined") {
    return (
      <div className="accept-invite">
        <div className="accept-invite__card">
          <h1>Invitation declined</h1>
          <p>You can close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite">
      <div className="accept-invite__card">
        <p className="accept-invite__eyebrow">You've been invited</p>
        <h1>
          Join <strong>{details?.orgName}</strong>
        </h1>
        <p className="accept-invite__meta">
          Invited by <strong>{details?.invitedByName}</strong> as{" "}
          <strong>{details?.role?.toLowerCase()}</strong>
        </p>
        <p className="accept-invite__meta">
          Invitation is for <strong>{details?.email}</strong>
        </p>

        {errorMessage && (
          <div className="accept-invite__error" role="alert">
            {errorMessage}
            {errorMessage.includes("different email") && authenticated && (
              <>
                {" "}
                <button
                  className="accept-invite__linkbtn"
                  onClick={() => logout()}
                >
                  Sign out and try again
                </button>
              </>
            )}
          </div>
        )}

        {!authenticated ? (
          <>
            <p className="accept-invite__hint">
              Sign in or create a Haven account to accept. The email you use
              must match the invitation.
            </p>
            <button
              className="accept-invite__btn accept-invite__btn--primary"
              onClick={() => login()}
            >
              Continue to sign in
            </button>
            <LegalLinks />
          </>
        ) : (
          <div className="accept-invite__actions">
            <button
              className="accept-invite__btn accept-invite__btn--primary"
              onClick={handleAccept}
              disabled={status === "accepting"}
            >
              {status === "accepting" ? "Accepting…" : "Accept invitation"}
            </button>
            <button
              className="accept-invite__btn"
              onClick={handleDecline}
              disabled={status === "declining"}
            >
              {status === "declining" ? "Declining…" : "Decline"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
